import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CustomStepper } from "../../../components";
import { STEPS, INITIAL_SCHOOL_DETAILS, formatCurrency } from "./pricingData";
import { AxiosInstance } from "../../../services/Api";
import Step1SelectPlan from "./steps/Step1SelectPlan";
import Step2AddOns from "./steps/Step2AddOns";
import Step3SchoolDetails from "./steps/Step3SchoolDetails";
import Step4ReviewPay from "./steps/Step4ReviewPay";
import styles from "./Pricing.module.css";

const STATIC_INCLUDES = [
  "One Time Setup",
  "Product Training",
  "Unlimited Cloud Storage",
  "24/7 support for all issues",
];

const AMC_INCLUDES = ["Cloud Support", "24/7 support for all issues"];

const mapApiPlan = (item) => {
  const slabs = (item.pricing || []).map((tier) => ({
    id: tier.pricing_id,
    // Slab configuration this tier belongs to — falls back to the plan's own
    // slab_id when the pricing row doesn't carry one.
    slabId: tier.slab_id ?? item.slab_id,
    label: `Up to ${tier.max_users} users`,
    price: formatCurrency(tier.plan_price),
    planPrice: Number(tier.plan_price) || 0,
    amcPrice: Number(tier.amc_price) || 0,
    maxUsers: tier.max_users,
    rangeNo: tier.range_no,
  }));

  // Always default to the first tier in the list, matching the first
  // "Slab Pricing by Users" row shown on the card.
  const defaultSlab = slabs[0];

  return {
    id: item.id,
    slabId: item.slab_id,
    planName: item.plan_name,
    badgeText: item.custom_badge_text || item.public_badge,
    badgeVariant: item.highlight_plan ? "popular" : "success",
    priceLabel: "Starting at",
    billingCycle: "year",
    description: item.plan_description,
    billingNote: "Billed annually. No hidden fees.",
    defaultSlabId: defaultSlab?.id,
    slabs,
    includes: [
      item.ai_count
        ? `${item.feature_count ?? 0}+ Core Features + ${item.ai_count} AI Features`
        : `${item.feature_count ?? 0}+ Core Features`,
      ...STATIC_INCLUDES,
    ],
    amcIncludes: AMC_INCLUDES,
    highlighted: Boolean(item.highlight_plan),
  };
};

const Pricing = () => {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);

  const [plans, setPlans] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [plansError, setPlansError] = useState("");

  const [addOnModules, setAddOnModules] = useState([]);
  const [addOnsLoading, setAddOnsLoading] = useState(true);
  const [addOnsError, setAddOnsError] = useState("");

  const [selectedPlanId, setSelectedPlanId] = useState(null);
  const [selectedSlabByPlan, setSelectedSlabByPlan] = useState({});
  const [selectedAddOnIds, setSelectedAddOnIds] = useState([]);
  const [schoolDetails, setSchoolDetails] = useState(INITIAL_SCHOOL_DETAILS);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [activeStep]);

  useEffect(() => {
    let isMounted = true;

    const fetchPlans = async () => {
      setPlansLoading(true);
      setPlansError("");
      try {
        const response = await AxiosInstance.post("saas/plan/card", {
          id: "",
        });

        if (!isMounted) return;

        if (response.data?.status) {
          const apiPlans = (response.data?.data || []).map(mapApiPlan);
          setPlans(apiPlans);

          if (apiPlans.length > 0) {
            const defaultPlan =
              apiPlans.find((plan) => plan.highlighted) || apiPlans[0];
            setSelectedPlanId(defaultPlan.id);
            setSelectedSlabByPlan(
              apiPlans.reduce(
                (acc, plan) => ({
                  ...acc,
                  [plan.id]:
                    plan.id === defaultPlan.id ? plan.defaultSlabId : null,
                }),
                {},
              ),
            );
          }
        } else {
          setPlansError(response.data?.message || "Failed to load plans");
        }
      } catch (err) {
        if (isMounted) {
          setPlansError(
            err.response?.data?.message ||
              "Failed to load plans. Please try again.",
          );
        }
      } finally {
        if (isMounted) setPlansLoading(false);
      }
    };

    fetchPlans();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
  const selectedSlab = selectedPlan?.slabs.find(
    (slab) => slab.id === selectedSlabByPlan[selectedPlanId],
  );

  // Add-ons belong to a slab configuration, so they follow whatever the user
  // has currently selected — the slab pricing row first, then its plan.
  const activeSlabId = selectedSlab?.slabId ?? selectedPlan?.slabId ?? null;

  useEffect(() => {
    let isMounted = true;

    if (!activeSlabId) {
      setAddOnModules([]);
      // Keep the spinner up while the plans (and therefore the slab) load.
      setAddOnsLoading(plansLoading);
      return undefined;
    }

    const fetchAddOns = async () => {
      setAddOnsLoading(true);
      setAddOnsError("");
      try {
        const response = await AxiosInstance.post("saas/view/add-ons", {
          slab_configuration_id: activeSlabId,
        });

        if (!isMounted) return;

        if (response.data?.status) {
          setAddOnModules(response.data?.data?.modules || []);
        } else {
          setAddOnsError(response.data?.message || "Failed to load add-ons");
        }
      } catch (err) {
        if (isMounted) {
          setAddOnsError(
            err.response?.data?.message ||
              "Failed to load add-ons. Please try again.",
          );
        }
      } finally {
        if (isMounted) setAddOnsLoading(false);
      }
    };

    fetchAddOns();

    return () => {
      isMounted = false;
    };
  }, [activeSlabId, plansLoading]);

  const goNext = () => setActiveStep((step) => Math.min(step + 1, 3));
  const goBack = () => setActiveStep((step) => Math.max(step - 1, 0));

  // BUG-002: when the plan changes, clear previously selected add-ons so
  // the user re-selects them for the newly chosen plan.
const handleSelectPlan = (planId) => {
  if (planId !== selectedPlanId) {
    setSelectedAddOnIds([]);

    setSelectedSlabByPlan((prev) => {
      const updated = { ...prev };
      plans.forEach((plan) => {
        if (plan.id === planId) {
          // Newly selected plan → check its first slab
          updated[plan.id] = plan.slabs[0]?.id ?? null;
        } else {
          // All other plans → uncheck everything (null = no radio checked)
          updated[plan.id] = null;
        }
      });
      return updated;
    });
  }
  setSelectedPlanId(planId);
};

  const handleSlabChange = (planId, slabId) => {
    setSelectedSlabByPlan((prev) => ({ ...prev, [planId]: slabId }));
  };

  const handleToggleAddOn = (addOnId) => {
    setSelectedAddOnIds((prev) =>
      prev.includes(addOnId)
        ? prev.filter((id) => id !== addOnId)
        : [...prev, addOnId],
    );
  };

  const handleFieldChange = (name, value) => {
    setSchoolDetails((prev) => ({ ...prev, [name]: value }));
  };

  // Plan slabs (500 / 1500 / 3000 / 4500 users) map positionally to the
  // slab configuration's range1..range4 price tiers returned by the API.
  const tierIndex = useMemo(() => {
    const index =
      selectedPlan?.slabs?.findIndex((s) => s.id === selectedSlab?.id) ?? -1;
    return index >= 0 ? index + 1 : 1;
  }, [selectedPlan, selectedSlab]);

  const addOns = useMemo(
    () =>
      addOnModules
        .filter((module) => module.can_edit === false)
        .map((module) => ({
          id: module.pricing_id,
          name: module.module_name,
          description: module.description, 
          price: Number(module[`price_range${tierIndex}`]) || 0,
        })),
    [addOnModules, tierIndex],
  );

  return (
    <div className={`page-container ${styles.pricingPage}`}>
      <section className={styles.heroSection}>
        <h1 className={`heroTitle ${styles.heroTitle}`}>
          Powering iFillip:{" "}
          <span className="gradientText">
            Every Part of Your
            <br />
            School, All in One Platform
          </span>
        </h1>
        <div className={styles.heroBullets}>
          <span className={styles.heroBullet}>
            <i className={styles.bulletSquare} />
            No surprise charges
          </span>
          <span className={styles.heroBullet}>
            <i className={styles.bulletSquare} />
            Scales with you
          </span>
        </div>
      </section>

      <div
        className={`${styles.mainCard} ${
          activeStep !== 0 ? styles.mainCardBare : ""
        }`}
      >
        <div className={styles.stepperWrap}>
          {/* Progress indicator only — the steps are not clickable. Moving
              between steps goes through the Back / Next buttons so the plan,
              slab and add-on selections stay in a valid order. */}
          <CustomStepper steps={STEPS} activeStep={activeStep} />
        </div>

        <div className={styles.stepContent}>
          {activeStep === 0 && (
            <Step1SelectPlan
              plans={plans}
              loading={plansLoading}
              error={plansError}
              selectedPlanId={selectedPlanId}
              selectedSlabByPlan={selectedSlabByPlan}
              onSelectPlan={handleSelectPlan}
              onSlabChange={handleSlabChange}
              onCompareFeatures={() => navigate("/pricing/compare-features")}
              onProceed={goNext}
              onContactSales={() => navigate("/contact")}
            />
          )}
          {activeStep === 1 && (
            <Step2AddOns
              addOns={addOns}
              loading={addOnsLoading}
              error={addOnsError}
              selectedAddOnIds={selectedAddOnIds}
              onToggleAddOn={handleToggleAddOn}
              plan={selectedPlan}
              slab={selectedSlab}
              onBack={goBack}
              onNext={goNext}
            />
          )}
          {activeStep === 2 && (
            <Step3SchoolDetails
              schoolDetails={schoolDetails}
              onFieldChange={handleFieldChange}
              addOns={addOns}
              selectedAddOnIds={selectedAddOnIds}
              plan={selectedPlan}
              slab={selectedSlab}
              onBack={goBack}
              onNext={goNext}
            />
          )}
          {activeStep === 3 && (
            <Step4ReviewPay
              schoolDetails={schoolDetails}
              onFieldChange={handleFieldChange}
              addOns={addOns}
              selectedAddOnIds={selectedAddOnIds}
              plan={selectedPlan}
              slab={selectedSlab}
              onBack={goBack}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default Pricing;
